import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../form/CustomTypography";
import { observer } from "mobx-react";

interface PopupGalleryProps {
  key: string;
  open: boolean;
  onClose: () => void;
  images: { id: number; src: string }[];
  title?: string;
  handleDeleteImage?: (id: number) => void;
}

const PopupGallery: React.FC<PopupGalleryProps> = ({
  key,
  open,
  onClose,
  handleDeleteImage,
  images,
  title,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const theme = useTheme();

  useEffect(() => {
    if (images.length === 0) {
      onClose();
    } else if (currentIndex >= images.length) {
      setCurrentIndex(images.length - 1);
    }
  }, [images, onClose, currentIndex]);

  const handleNext = () => {
    setCurrentIndex((prev) => {
      const newIndex = (prev + 1) % images.length;
      thumbnailRefs.current[newIndex]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
      return newIndex;
    });
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => {
      const newIndex = (prev - 1 + images.length) % images.length;
      thumbnailRefs.current[newIndex]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
      return newIndex;
    });
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
    thumbnailRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  return (
    <Dialog
      key={key || "popupGallery"}
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDialog-container": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingLeft: 5,
        }}
      >
        <CustomTypography variant="heading4">{title}</CustomTypography>
        <IconButton sx={{ left: "12px" }} aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 5 }}>
        <Box
          sx={{
            position: "relative",
            backgroundColor: theme.palette.customColors.grayBackGroundColor,
            borderRadius: 2,
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
            minWidth: "30vw",
          }}
        >
          {images.length > 0 && (
            <>
              <IconButton
                sx={{
                  position: "absolute",
                  bottom: 16,
                  right: 16,
                  bgcolor: theme.palette.customColors.pinkLight,
                }}
                disabled={!handleDeleteImage}
                onClick={() => handleDeleteImage?.(images[currentIndex]?.id)}
                size="small"
              >
                <DeleteIcon
                  sx={{ color: theme.palette.customColors.pinkDark }}
                />
              </IconButton>

              <IconButton
                onClick={handlePrev}
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1,
                  border:
                    "1px solid " + theme.palette.customColors.greyDarkborder,
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>

              <Box
                component="img"
                src={images[currentIndex]?.src}
                alt={`Image ${currentIndex}`}
                sx={{ minHeight: 270, maxWidth: 220, objectFit: "contain" }}
              />

              <IconButton
                onClick={handleNext}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1,
                  border:
                    "1px solid " + theme.palette.customColors.greyDarkborder,
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </>
          )}
        </Box>

        {images?.length > 0 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                overflowX: "auto",
                gap: 1,
                maxWidth: 350,
                px: 1,
                pb: 1.5,
                scrollbarWidth: "thin",
              }}
            >
              {images?.map(({ src }, idx) => (
                <Box
                  key={idx}
                  ref={(el: HTMLDivElement | null) =>
                    (thumbnailRefs.current[idx] = el)
                  }
                >
                  <Box
                    onClick={() => handleThumbnailClick(idx)}
                    sx={{
                      border:
                        currentIndex === idx
                          ? "2px solid" + theme.palette.customColors.blackBorder
                          : "1px solid " +
                            theme.palette.customColors.greyDarkborder,
                      borderRadius: 1,
                      overflow: "hidden",
                      cursor: "pointer",
                      width: 70,
                      height: 70,
                      backgroundColor:
                        currentIndex === idx
                          ? theme.palette.customColors.grayBackGroundColor
                          : "transparent",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={src}
                      alt={`Thumbnail ${idx}`}
                      sx={{
                        width: "70%",
                        height: "70%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default observer(PopupGallery);
